/** 素材库音频上传约束（mp3/wav，≤15MB，时长 2–15 秒） */
export const LIBRARY_AUDIO_MAX_SIZE_BYTES = 15 * 1024 * 1024;
export const LIBRARY_AUDIO_MIN_DURATION_SEC = 2;
export const LIBRARY_AUDIO_MAX_DURATION_SEC = 15;
export const LIBRARY_AUDIO_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/x-wav';

const AUDIO_MIME_TYPES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
]);

export const isLibraryAudioFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  if (/\.(mp3|wav)$/.test(name)) return true;
  return AUDIO_MIME_TYPES.has(file.type.toLowerCase());
};

export const getAudioFileDurationSec = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(duration)) {
        reject(new Error('无法读取音频时长'));
        return;
      }
      resolve(duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法读取音频时长'));
    };
    audio.src = url;
  });

export interface LibraryAudioValidationResult {
  valid: File[];
  invalidFormat: File[];
  oversized: File[];
  invalidDuration: Array<{ file: File; duration: number | null }>;
}

export const validateLibraryAudioFiles = async (
  files: File[],
): Promise<LibraryAudioValidationResult> => {
  const result: LibraryAudioValidationResult = {
    valid: [],
    invalidFormat: [],
    oversized: [],
    invalidDuration: [],
  };

  for (const file of files) {
    if (!isLibraryAudioFile(file)) continue;

    const extOk = /\.(mp3|wav)$/i.test(file.name);
    const mimeOk = AUDIO_MIME_TYPES.has(file.type.toLowerCase());
    if (!extOk && !mimeOk) {
      result.invalidFormat.push(file);
      continue;
    }

    if (file.size > LIBRARY_AUDIO_MAX_SIZE_BYTES) {
      result.oversized.push(file);
      continue;
    }

    try {
      const duration = await getAudioFileDurationSec(file);
      if (
        duration < LIBRARY_AUDIO_MIN_DURATION_SEC ||
        duration > LIBRARY_AUDIO_MAX_DURATION_SEC
      ) {
        result.invalidDuration.push({ file, duration });
        continue;
      }
      result.valid.push(file);
    } catch {
      result.invalidDuration.push({ file, duration: null });
    }
  }

  return result;
};
